/* eslint-disable max-len */
import {h} from 'preact';
import {useState} from "preact/hooks";
import clsx from "clsx";
import { formatMoney } from 'utils/storefront/format-resource';
import Swatches from "./swatches";
import ProductBadges from '../product/product-badges';

/**
 * Validates the passed product object and throws an error if invalid.
 * @param {Object} product - The product object.
 * @returns {boolean} - Returns true if the product object is valid.
 */
const handleErrors = product => {
  if (!product) {
    throw new Error('No product object passed to ProductCard');
  }
  return true;
};

/**
 * ProductCard component to display product information.
 * @param {Object} props - Component properties.
 * @param {Object} props.product - The product details.
 * @param {boolean} props.portraitOverwrite - Overwrite the portrait setting.
 * @param {String} props.classes - Card css classes.
 * @returns {JSX.Element} The ProductCard component.
 */
const ProductCard = ({product, portraitOverwrite, classes, isB2B}) => {
  handleErrors(product);
  const isPLP = window.location.pathname.includes('/collections')
  const portraitRatioClass = isPLP ? 'aspect-[174/229] md:aspect-[342/454]' : 'aspect-[164/217] md:aspect-[342/454]'
  const landscapeRatioClass = isPLP ? 'aspect-[351/231] md:aspect-[694/454]' : 'aspect-[330/217] md:aspect-[694/454]'
  let portrait = !!product?.metafields.portrait;
  if (portraitOverwrite !== null && portraitOverwrite !== undefined) {
    portrait = portraitOverwrite
  }
  const [currentVariant, setCurrentVariant] =
    useState(product.variants[0]);

  const multipleSizes = product.optionsWithValues.some(o => o.name.toLowerCase() === 'size' && o.values.length > 1)
  const onlyColorOption = product.optionsWithValues.every(o => o.name.toLowerCase() === 'color')
  /**
   * Handler for changing the product variant.
   * @param {string} variantId - The ID of the variant to switch to.
   */
  const handleVariantChange = variantId => {
    const variant = product.variants.find(v => v.id === variantId)
      || product.variants[0];
    setCurrentVariant(variant);
  };

	const compareAgainstRetailPrice = isB2B && currentVariant?.retailPrice?.amount > 0

	const price = () => {
		const className = compareAgainstRetailPrice
			? 'text-dark-green ml-2'
			: currentVariant.compare_at_price?.amount
			? 'text-red ml-2'
			: '';

      let amount = currentVariant?.price.replace(/[^\d.]/g, '');
      const currencyCode = window.TMW?.localization?.currencyCode;

      if(product.sellInSets){
        amount *= parseInt(product.sellInSets , 10 || 1)
      }
      
		return (
			<span className={`price normal-case ${className}`}>
				{compareAgainstRetailPrice
					? `${formatMoney({amount, currencyCode})} ${window.TMW.Strings.product.b2b}`
					: formatMoney({amount, currencyCode })}
			</span>
		);
	};

	const compareAtPrice = () => {
    const amount = compareAgainstRetailPrice
      ? currentVariant?.retailPrice?.amount?.toString().replace(/[^\d.]/g, '')
      : currentVariant?.compare_at_price?.amount?.toString().replace(/[^\d.]/g, '');

    if (!amount) return null;

    let numericAmount = parseFloat(amount);
    if (product.sellInSets) {
      numericAmount *= parseInt(product.sellInSets, 10) || 1;
    }

    const currencyCode = window.TMW?.localization?.currencyCode;

    return (
      <span className="compareAtPrice">
        <del>{formatMoney({ amount: numericAmount.toFixed(2), currencyCode })}</del> 
      </span>
    );
  };


  return (
    <article className={classes}>
      <a className="block relative text-primary no-underline" href={currentVariant.url}>
        {currentVariant.image?.src ? (
          <div className={clsx(`group`, product?.blendMode && 'bg-grey-5')}>
            <img
              className={
                clsx("w-full image object-cover mx-auto", 
                  product?.blendMode && 'mix-blend-darken',
                  product.images.length > 1 && "group-hover:hidden",
                  portrait ? portraitRatioClass : landscapeRatioClass)
              }
              alt={currentVariant.image.alt}
              src={currentVariant.image.src}
              width={portrait ? 342 : 694}
              height="454"
              srcSet={currentVariant.image.srcSet}
              loading="lazy"
            />
            {product.images.length > 1 && (
              <img
                className={`w-full hidden group-hover:block 
                image object-cover mix-blend-darken mx-auto  
                ${product?.blendMode && 'mix-blend-darken'}
                ${portrait ? portraitRatioClass : landscapeRatioClass}`}
                alt={product.images[1].alt}
                src={product.images[1].src}
                width={portrait ? 342 : 694}
                height="454"
                srcSet={product.images[1].srcSet}
                loading="eager"
              />
            )}
          </div>
        ) : (
          <div dangerouslySetInnerHTML={{__html: product.placeholder_svg}}/>
        )}
        <div className='px-3 mt-2 md:mt-0'>
          {product.metafields.badges && (
            <ProductBadges badges={product?.metafields?.badges}/>
          )}
        </div>
      </a>
      <div className="px-3 py-2 md:py-3 md:px-5">
        <a className="block relative text-primary no-underline" href={currentVariant.url}>
          <h2 className="utility-2-bold">{product.title}</h2>
          <p className="utility-2 font-medium">
						{compareAtPrice()}
						{price()}
						{!onlyColorOption && multipleSizes && (
							<span className="text-utility-2 text-secondary">{window.TMW.Strings.product.multipleSizes}</span>
						)}
          </p>
        </a>
        <Swatches
          product={product}
          currentVariant={currentVariant}
          handleVariantChange={handleVariantChange}
          limit={3}/>
      </div>
    </article>
  );
};

export default ProductCard;
